import "@fontsource/ubuntu/300.css";
import "@fontsource/ubuntu/400.css";
import "@fontsource/ubuntu/500.css";
import "@fontsource/ubuntu/700.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { LandingPage } from "./pages/Landing/Landing";

const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/idp_callback",
    lazy: async () => {
      const { IdPCallbackPage } = await import("./pages/IdPCallback");
      return { Component: IdPCallbackPage };
    },
  },
  {
    path: "/",
    lazy: async () => {
      const { AdminLayout } = await import("./containers/AdminLayout");
      return { Component: AdminLayout };
    },
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/overview",
        lazy: async () => {
          const { OverviewPage } = await import("./pages/Overview");
          return { Component: OverviewPage };
        },
      },
      {
        path: "/resources",
        lazy: async () => {
          const { MediaMaterial } = await import("./components/MediaMaterial");
          return { Component: MediaMaterial };
        },
      },
      {
        path: "/threat_encyclopedia",
        lazy: async () => {
          const { ThreatEncyclopediaPage } = await import("./pages/ThreatEncyclopedia");
          return { Component: ThreatEncyclopediaPage };
        },
      },
      {
        path: "/organization",
        lazy: async () => {
          const { OrganizationPage } = await import("./pages/Organization");
          return { Component: OrganizationPage };
        },
      },
      {
        path: "/product_licensing",
        lazy: async () => {
          const { LicenseListPage } = await import("./pages/LicenseList");
          return { Component: LicenseListPage };
        },
      },
      {
        path: "/file/software",
        lazy: async () => {
          const { SoftwarePage } = await import("./pages/ComponentList");
          return { Component: SoftwarePage };
        },
      },
      {
        path: "/file/patterns",
        lazy: async () => {
          const { PatternsPage } = await import("./pages/ComponentList");
          return { Component: PatternsPage };
        },
      },
      {
        path: "/file/documentation",
        lazy: async () => {
          const { DocumentationPage } = await import("./pages/ComponentList");
          return { Component: DocumentationPage };
        },
      },
      {
        path: "/customer_support",
        lazy: async () => {
          const { CustomerSupportPage } = await import("./pages/CustomerSupport");
          return { Component: CustomerSupportPage };
        },
      },
      {
        path: "/partner_portal",
        lazy: async () => {
          const { PartnerPortalPage } = await import("./pages/PartnerPortal");
          return { Component: PartnerPortalPage };
        },
      },
    ],
  },
  {
    path: "/license_expired",
    lazy: async () => {
      const { LicenseExpired } = await import("./components/Error/LicenseExpired");
      return { Component: LicenseExpired };
    },
  },
  {
    path: "*",
    lazy: async () => {
      const { PageNotFound } = await import("./components/Error/PageNotFound");
      return { Component: PageNotFound };
    },
  },
]);

const primaryColor = "#1b0b38";
const secondaryColor = "#D71920";
const theme = createTheme({
  palette: {
    primary: {
      main: primaryColor,
    },
    secondary: {
      main: secondaryColor,
    },
    error: {
      main: "#D71920",
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: `"Ubuntu", "Roboto", "Helvetica", "Arial", sans-serif`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            borderRadius: "10px",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "28px",
          textTransform: "none",
          "&:hover": {
            boxShadow: "none",
            opacity: 0.78,
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: secondaryColor,
            opacity: 0.78,
          },
        },
        startIcon: {
          marginRight: 2,
        },
      },
    },
    MuiLink: {
      root: {
        color: "#241540 !important",
      },
    },
  },
});

const App = () => {
  return (
    <SnackbarProvider>
      <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
      </ThemeProvider>
    </SnackbarProvider>
  );
};

export default App;
